import { FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/form-control';
import { ModalContent, ModalBody, ModalHeader, ModalCloseButton, Flex, Input, Button, Text } from '@chakra-ui/react';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IToastType } from '@/enums/ToastType';
import { useStores } from '@/hooks/useStores';
import { useToast } from '@/hooks/useToast';
import { IError } from '@/interfaces/json-api';
import { EMAIL_REGEX } from '@/utils/constants';

interface IForgotPasswordProps {
  onClose: () => void;
}

export const ForgotPassword: FC<IForgotPasswordProps> = ({ onClose }) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();
  const toast = useToast();
  const { t } = useTranslation();
  const { store } = useStores();
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  async function onSubmit({ email }) {
    try {
      const body = {
        data: {
          type: 'password',
          attributes: {
            email,
          },
        },
      };

      await store.request('password', 'POST', body);
      toast({ status: IToastType.SUCCESS, title: t('loginPopup.notifications.successfullySent') });
      setIsFormSubmitted(true);
    } catch (submitError) {
      if (submitError instanceof Array) {
        const errorMessages = submitError.map((error: IError) => error.detail).join('');

        toast({ status: IToastType.ERROR, title: errorMessages });
      } else {
        const errorMessage = submitError.message.toString();

        toast({ status: IToastType.ERROR, title: errorMessage });
      }
    }
  }

  if (isFormSubmitted) {
    return (
      <ModalContent px={{ base: 0, sm: 4, lg: 18 }} py={{ base: 10, md: 14 }}>
        <ModalCloseButton fontSize={{ base: 'md', lg: 'xl' }} m={4} />
        <ModalHeader fontSize={{ base: '2xl', lg: '3xl' }} lineHeight={9} fontWeight="bold" mb={2}>
          {t('loginPopup.forgotPasswordSubmitted.heading')}
        </ModalHeader>
        <ModalBody>
          <Text mb="10">{t('loginPopup.forgotPasswordSubmitted.description', { email: watch('email') })}</Text>
          <Flex flexDirection="column">
            <Button onClick={onClose} w="full" type="button" mt={8} mb={4}>
              {t('loginPopup.forgotPasswordSubmitted.confirmButton')}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    );
  }

  return (
    <ModalContent px={{ base: 0, sm: 4, lg: 18 }} py={{ base: 10, md: 14 }}>
      <ModalCloseButton fontSize={{ base: 'md', lg: 'xl' }} m={4} />
      <ModalHeader fontSize={{ base: '2xl', lg: '3xl' }} lineHeight={9} fontWeight="bold" mb={2}>
        {t('loginPopup.forgotPasswordModal.heading')}
      </ModalHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <Text mb="10">{t('loginPopup.forgotPasswordModal.description')}</Text>
          <Flex flexDirection="column">
            <FormControl mb={8} variant="floating" isInvalid={Boolean(errors.email)}>
              <Input
                placeholder=" "
                {...register('email', {
                  required: t('form.required'),
                  pattern: {
                    value: EMAIL_REGEX,
                    message: t('form.invalidEmail'),
                  },
                })}
                variant="flushed"
                errorBorderColor="none"
                autoComplete="email"
                _invalid={{ boxShadow: 'none' }}
              />
              <FormLabel>{t('loginPopup.loginModal.email')}</FormLabel>
              <FormErrorMessage>{t('form.required')}</FormErrorMessage>
            </FormControl>

            <Button w="full" type="submit" mt={8} mb={4}>
              {t('loginPopup.forgotPasswordModal.submitButton')}
            </Button>
          </Flex>
        </ModalBody>
      </form>
    </ModalContent>
  );
};
